/**
 * IndicatorLegend — identity and visibility for every indicator on the chart.
 *
 * Always present: with more than one series, colour alone must never be the
 * only thing carrying identity, and the palette's lowest-contrast slots are
 * only legal alongside visible labels. Doubles as the visibility control.
 */
import { formatValue } from '../../lib/signalSeries';

function Swatch({ series }) {
  return (
    <span
      className="inline-block h-0.5 w-4 shrink-0 rounded"
      style={{
        background: series.color,
        opacity: series.declared === false ? 0.55 : 1,
      }}
    />
  );
}

function Entry({ series, on, onToggle, value }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(series.id)}
      className={`group flex items-center gap-1.5 rounded px-1.5 py-1 text-left transition-colors hover:bg-muted/60 ${
        on ? '' : 'opacity-40'
      }`}
      title={on ? 'Hide' : 'Show'}
    >
      <Swatch series={series} />
      <span className="truncate text-[11px] text-muted-foreground group-hover:text-foreground">
        {series.label}
      </span>
      {value !== undefined && (
        <span className="ml-auto shrink-0 font-mono text-[11px] font-semibold text-foreground">
          {formatValue(series, value)}
        </span>
      )}
    </button>
  );
}

export default function IndicatorLegend({
  series = [],
  visible = {},
  onToggle,
  onToggleMany,
  row,
}) {
  const declared = series.filter((s) => s.declared !== false);
  const dependencies = series.filter((s) => s.declared === false);
  const depsOn = dependencies.some((s) => visible[s.id]);

  if (!series.length) return null;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-x-2 sm:grid-cols-3 xl:grid-cols-4">
        {declared.map((s) => (
          <Entry
            key={s.id}
            series={s}
            on={visible[s.id] !== false}
            onToggle={onToggle}
            value={row ? row[s.id] : undefined}
          />
        ))}
      </div>

      {dependencies.length > 0 && (
        <div className="border-t border-border/50 pt-2">
          <button
            type="button"
            onClick={() =>
              onToggleMany(dependencies.map((s) => s.id), !depsOn)
            }
            className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-muted/60"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {depsOn ? 'Hide' : 'Show'} dependencies ({dependencies.length})
            </span>
          </button>
          {depsOn && (
            <div className="grid grid-cols-2 gap-x-2 sm:grid-cols-3 xl:grid-cols-4">
              {dependencies.map((s) => (
                <Entry
                  key={s.id}
                  series={s}
                  on={visible[s.id] !== false}
                  onToggle={onToggle}
                  value={row ? row[s.id] : undefined}
                />
              ))}
            </div>
          )}
          <p className="px-1.5 pt-1 text-[10px] leading-snug text-muted-foreground">
            Attributes the engine computed as dependencies — the screener never
            declared them directly.
          </p>
        </div>
      )}
    </div>
  );
}
